import http from "./http";
import type {
  AiModelsResponse,
  AiEditRunDetails,
  AiSectionHistoryItem,
  SectionAiEditPayload,
  BrandVoiceResponse,
} from "@/types/aiEditor";

export const aiSectionEditService = {
  /**
   * Fetch available OpenAI and local Ollama models.
   */
  async getModels(): Promise<AiModelsResponse> {
    const { data } = await http.get<AiModelsResponse>("/api/reports/ai-editor/models");
    return data;
  },

  /**
   * Queue an AI revision for a specific section.
   */
  async queueSectionEdit(
    reportId: number | string,
    sectionKey: string,
    payload: SectionAiEditPayload
  ): Promise<{ run_id: string; status: string; message: string }> {
    const { data } = await http.post(`/api/reports/${reportId}/sections/${sectionKey}/ai-edit`, payload);
    return data;
  },

  /**
   * Poll status of an async AI edit run.
   */
  async pollRun(runId: string): Promise<AiEditRunDetails> {
    const { data } = await http.get<{ run: AiEditRunDetails }>(`/api/reports/ai-edit-runs/${runId}`);
    return data.run;
  },

  /**
   * Get version history for a section.
   */
  async getHistory(reportId: number | string, sectionKey: string): Promise<AiSectionHistoryItem[]> {
    const { data } = await http.get<{ history: AiSectionHistoryItem[] }>(
      `/api/reports/${reportId}/sections/${sectionKey}/ai-edit/history`
    );
    return data.history;
  },

  /**
   * Revert section to previous version.
   */
  async undo(
    reportId: number | string,
    sectionKey: string
  ): Promise<{ restored_version: number | null; message: string }> {
    const { data } = await http.post(`/api/reports/${reportId}/sections/${sectionKey}/ai-edit/undo`);
    return data;
  },

  /**
   * Reset section to original report data.
   */
  async reset(reportId: number | string, sectionKey: string): Promise<{ message: string }> {
    const { data } = await http.post(`/api/reports/${reportId}/sections/${sectionKey}/ai-edit/reset`);
    return data;
  },

  /**
   * Restore section to a specific version number.
   */
  async restoreVersion(
    reportId: number | string,
    sectionKey: string,
    version: number
  ): Promise<{ restored_version: number; message: string }> {
    const { data } = await http.post(`/api/reports/${reportId}/sections/${sectionKey}/ai-edit/restore`, {
      version,
    });
    return data;
  },

  /**
   * Enhance draft prompt using AI.
   */
  async enhancePrompt(
    reportId: number | string,
    sectionKey: string,
    prompt: string,
    provider?: string,
    model?: string
  ): Promise<string> {
    const { data } = await http.post<{ enhanced_prompt: string }>(
      `/api/reports/${reportId}/sections/${sectionKey}/ai-edit/enhance-prompt`,
      { prompt, provider, model }
    );
    return data.enhanced_prompt;
  },

  /**
   * Get client AI brand voice.
   */
  async getBrandVoice(clientId: string): Promise<string> {
    const { data } = await http.get<BrandVoiceResponse>(`/api/clients/${clientId}/ai-profile`);
    return data.brand_voice || "";
  },

  /**
   * Update client AI brand voice.
   */
  async updateBrandVoice(clientId: string, brandVoice: string): Promise<string> {
    const { data } = await http.put<BrandVoiceResponse>(`/api/clients/${clientId}/ai-profile`, {
      brand_voice: brandVoice,
    });
    return data.brand_voice || "";
  },
};
